import { useState } from 'react';
import { Field, Input } from '@/ui';
import { usersApi } from '@/api';
import type { User } from '@/types';
import { StepLayout } from '../StepLayout';

interface CompanyInfoStepProps {
  profile: User;
  onSaved: (user: User) => void;
  onSkip: () => void;
}

export function CompanyInfoStep({ profile, onSaved, onSkip }: CompanyInfoStepProps) {
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  const [companyName, setCompanyName] = useState(profile.companyName ?? '');
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [licenseNumber, setLicenseNumber] = useState(profile.licenseNumber ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Next" is enabled the moment the user has typed *something* — empty
  // fields stay empty in the DB. The reason this isn't fully required: an
  // inspector who already typed their company in Settings or signed up via
  // SSO with a name pre-filled shouldn't be force-edited.
  const handleNext = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await usersApi.updateProfile({
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        companyName: companyName.trim() || null,
        phone: phone.trim() || null,
        licenseNumber: licenseNumber.trim() || null,
      });
      if (resp.success && resp.data) {
        onSaved(resp.data);
      } else {
        setError(resp.error || 'Failed to save.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepLayout
      title="Tell us about your business"
      subtitle="This appears on your reports, emails, and booking page. You can change it later in Settings."
      onPrimary={handleNext}
      primaryLabel="Save & continue"
      primaryLoading={saving}
      onSecondary={onSkip}
      secondaryLabel="Skip for now"
      error={error}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name">
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jane"
            autoComplete="given-name"
          />
        </Field>
        <Field label="Last name">
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            autoComplete="family-name"
          />
        </Field>
        <Field label="Company name" className="sm:col-span-2">
          <Input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Doe Home Inspections"
            autoComplete="organization"
          />
        </Field>
        <Field label="Phone" hint="Shown on reports and booking confirmations">
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
        </Field>
        <Field label="License number" optional>
          <Input
            value={licenseNumber}
            onChange={(e) => setLicenseNumber(e.target.value)}
            placeholder="HI-12345"
          />
        </Field>
      </div>
    </StepLayout>
  );
}
